import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { RequestService } from '@df/api/request/request.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { ISizeName } from '@df/core/service/client';
import { Status } from '@df/core/status';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { ValidatePatternDirective } from '@df/ui/form/validate-pattern.directive';
import { DfCmsModal } from '@df/ui/modal/df-cms-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { ValidateDobRangeDirective } from '../../../ui/form/validate/validate-dob-range.directive';
import { ResultComponent } from '../../../ui/resut/result.component';
import { AntavoService } from '../../antavo/antavo.service';
import { PwaLoyaltyTermsModalInitiatorService } from './pwa-loyalty-terms-modal.initiator.service';

export namespace NPwaLoyaltyTermsModal {
    export const MODAL_ID = 'pwa-loyalty-terms-modal';

    export type IBlockData = Catalog.IBlockData<IData>;

    export interface IData {
        notOpenLoyaltyTermsModal: boolean;
        title: string;
        description: string;
        checkboxTitle: string;
        checkboxDescription?: string;
        continueButtonText: string;
        ageRestrictionMessage?: string;
        forbiddenLocations?: string;
    }
}

@DfCmsModal({
    id: NPwaLoyaltyTermsModal.MODAL_ID,
    position: 'bottom',
    name: 'PWA Loyalty Terms',
    config: (sizeId: ISizeName) => ({
        width: '100vw',
        height: '100vh',
        minWidth: '100%',
        minHeight: '100%',
        modalClass: 'p-a-0',
        closeOnNavigation: false,
        closeOnBackdrop: false,
        closeOnEscape: false,
        autoCreate: true
    }),
    initiators: [PwaLoyaltyTermsModalInitiatorService],
    reopenPolicy: NDfModal.ReopenPolicy.last,
    sizeRestriction: sizeId => ['S', 'M']
})
@ICmsComponent<PwaLoyaltyTermsModalComponent>({
    id: NPwaLoyaltyTermsModal.MODAL_ID,
    block: {
        url: NPwaLoyaltyTermsModal.MODAL_ID
    },
    onDemandOnly: true,
    name: 'PWA Loyalty Terms',
    fields: [
        <NICmsContentField.IToggleField>{
            key: 'notOpenLoyaltyTermsModal',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Not open loyalty terms modal',
            default: false
        },
        <NICmsContentField.IField>{
            key: 'title',
            type: NICmsContentField.EFieldType.text,
            title: 'Title',
            default: 'ACCOUNT UPGRADE',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'description',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Description',
            default: `You can use your lemieux.com account to access LeMieux Insiders - it's free. Simply tick the box to upgrade.`,
            required: true
        },
        <NICmsContentField.IField>{
            key: 'checkboxDescription',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Checkbox description',
            default: 'Read our Community and Rewards T&Cs.'
        },
        <NICmsContentField.IField>{
            key: 'checkboxTitle',
            type: NICmsContentField.EFieldType.text,
            title: 'Checkbox title',
            default: 'I accept the Community and Rewards T&Cs',
            required: true
        },

        <NICmsContentField.IField>{
            key: 'continueButtonText',
            type: NICmsContentField.EFieldType.text,
            title: 'Continue button text',
            default: 'Continue',
            required: true
        },
        <NICmsContentField.IField>{
            key: 'ageRestrictionMessage',
            type: NICmsContentField.EFieldType.text,
            title: 'Age restriction message',
            default: 'You must be at least 13 years old to accept the terms and conditions.'
        },
        <NICmsContentField.IField>{
            key: 'forbiddenLocations',
            type: NICmsContentField.EFieldType.textarea,
            title: 'Forbidden locations (comma-separated)',
            default: '/terms-and-conditions',
            description:
                'Pages where the modal should not appear. Provide comma-separated list of URLs (e.g., /terms-and-conditions, /privacy-policy)'
        }
    ]
})
@Component({
    selector: 'pwa-loyalty-terms-modal',
    templateUrl: './pwa-loyalty-terms-modal.html',
    styleUrls: ['pwa-loyalty-terms-modal.scss'],
    standalone: true,
    imports: [
        ICmsClientModule,
        CommonModule,
        TranslationModule,
        CheckboxComponent,
        InputWrapComponent,
        InputWrapBodyDirective,
        MuiInputDirective,
        FormsModule,
        ResultComponent,
        ValidateDobRangeDirective,
        ValidatePatternDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PwaLoyaltyTermsModalComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NPwaLoyaltyTermsModal.IData;

    protected acceptLoyaltyTerms = false;
    protected submitPath = Nexus.Api.Customer.Save.path;
    protected submitStatus = new Status();
    protected nickname = '';

    private requestService = inject(RequestService);
    private router = inject(Router);
    private antavoService = inject(AntavoService);

    protected minDate = formatDate(new Date(new Date().getFullYear() - 120, 0, 1), 'yyyy-MM-dd', 'en_US'); // 120 years ago
    protected maxDate = formatDate(new Date(), 'yyyy-MM-dd', 'en_US'); // Today

    private _dob: Date | undefined;

    constructor(private modalRef: ModalRef<NPwaLoyaltyTermsModal.IData>) {
        super();
    }

    protected close(): void {
        this.modalRef?.close();
    }

    get dob(): Date | undefined {
        return this._dob;
    }

    set dob(dob: Date | undefined) {
        this._dob = dob;
    }

    protected isUserUnder13(): boolean {
        const dobToCheck = this.dob ? new Date(this.dob) : null;

        if (!dobToCheck) {
            return false;
        }

        const today = new Date();
        // Set time to midnight to ensure we're only comparing dates, not times
        today.setHours(0, 0, 0, 0);

        const thirteenYearsAgo = new Date(today.getFullYear() - 13, today.getMonth(), today.getDate());
        thirteenYearsAgo.setHours(0, 0, 0, 0);

        // Set dobToCheck to midnight as well for consistent comparison
        const dobDate = new Date(dobToCheck);
        dobDate.setHours(0, 0, 0, 0);

        return dobDate > thirteenYearsAgo;
    }

    protected async confirm(): Promise<void> {
        if (this.isUserUnder13()) {
            this.submitStatus.error =
                this.content?.ageRestrictionMessage || 'You must be at least 13 years old to accept the terms and conditions';
            return;
        }
        if (this.acceptLoyaltyTerms) {
            const data: { accepted_loyalty: boolean; dob?: string; nickname?: string } = {
                accepted_loyalty: true
            };

            // Always include DOB since it's now required
            if (this.dob) {
                data.dob = formatDate(this.dob, 'yyyy-MM-dd', 'en_US');
            }

            // Include nickname
            if (this.nickname) {
                data.nickname = this.nickname;
            }

            await this.requestService.submitForm(
                undefined,
                this.submitStatus,
                {
                    path: this.submitPath,
                    data
                },
                async () => {
                    this.close();
                    await this.checkFirstMission();
                }
            );
        }
    }

    private async checkFirstMission(): Promise<void> {
        const mission = await this.antavoService.getFirstMission();
        if (mission && mission.completions !== 1) {
            // Mission exists and is not completed, redirect to introduction
            this.router.navigateByUrl('/first-mission-introduction');
        } else {
            console.warn('Mission not found or already completed');
        }
    }
}
